import { useId } from 'react'
import { Link } from 'react-router'
import { ChangeText } from './ChangeText'
import { EmptyState } from './EmptyState'
import { Skeleton } from './Skeleton'
import { Tabs } from './Tabs'
import { SymbolPicker } from './SymbolPicker'
import { LiveNumber } from './LiveNumber'
import { loginHref } from '../lib/analytics'
import styles from './WatchlistPanel.module.css'

export interface WatchItem {
  symbol: string
  exchange: 'NSE' | 'BSE'
  /** `null` when prices are not connected. */
  ltp: number | null
  changePercent: number | null
  /** Short label of today's filing, e.g. "Order win". */
  filing?: string | null
}

export interface WatchlistPanelProps {
  lists: Array<{ id: string; name: string }>
  activeId: string
  onSelect: (id: string) => void
  items: WatchItem[]
  state?: 'ready' | 'loading' | 'signed-out' | 'error'
  /** Shown above the list when prices are missing. */
  pricesConnected?: boolean
  onCreateList?: () => void
  onSearchAdd?: (q: string) => void
  editHref?: string
  /** Accessible name when several panels share a page (defaults to the heading). */
  label?: string
  /** false = hide the LTP / change column entirely (prices not licensed). */
  showPrices?: boolean
  /** Adding / creating state (disables the add form). */
  busy?: boolean
  /** Stock currently open (highlighted, aria-current). */
  activeSymbol?: string
}

export function WatchlistPanel(props: WatchlistPanelProps) {
  const { lists, activeId, onSelect, items, state = 'ready', pricesConnected = true, onCreateList, onSearchAdd, editHref, label, showPrices = true, busy = false, activeSymbol } = props
  const uid = useId()
  const titleId = `${uid}-title`, rowsId = `${uid}-rows`
  return (
    <section aria-labelledby={label ? undefined : titleId} aria-label={label} className={styles.panel}>
      <div className={styles.head}>
        <h2 id={titleId} className={styles.title}>Watchlist</h2>
        {editHref && <Link to={editHref} className={styles.edit}>Edit</Link>}
      </div>
      {lists.length > 0 && (
        <div className={styles.lists}>
          <Tabs label="Watchlists" variant="pill" tabs={lists.map(l => ({ id: l.id, label: l.name }))} value={activeId} onChange={onSelect} controls={rowsId} />
          {onCreateList && <button type="button" className={styles.add} onClick={onCreateList} aria-label="New watchlist">+</button>}
        </div>
      )}
      {onSearchAdd && (
        <div className={styles.picker}>
          <SymbolPicker label="Search and add to watchlist" placeholder="Search & add (symbol or name)" disabled={busy} onSelect={h => onSearchAdd(h.symbol)} />
        </div>
      )}
      <div className={`${styles.colHead} ${showPrices ? '' : styles.noPrices}`} aria-hidden="true"><span>Symbol · today’s filing</span>{showPrices && <span>LTP / Chg</span>}</div>
      {showPrices && !pricesConnected && state === 'ready' && items.length > 0 && (
        <p className={styles.notice}>Live prices: data not connected</p>
      )}
      <div id={rowsId} className={styles.rows}>
        {state === 'loading' && <div className={styles.pad}><Skeleton variant="row" lines={6} label="Loading watchlist" /></div>}
        {state === 'signed-out' && <EmptyState title="Sign in to see your watchlist" compact action={<Link to={loginHref('/watchlist')}>Sign in</Link>} />}
        {state === 'error' && <EmptyState kind="error" title="Could not load watchlist" compact />}
        {state === 'ready' && items.length === 0 && (
          <EmptyState title="No stocks in this list yet" compact>Search above to add up to your plan’s limit.</EmptyState>
        )}
        {state === 'ready' && items.length > 0 && (
          <ul className={styles.list}>
            {items.map(w => (
              <li key={`${w.exchange}:${w.symbol}`}>
                <Link to={`/stock/${encodeURIComponent(w.symbol)}`} aria-current={w.symbol === activeSymbol ? 'page' : undefined}
                  className={`${styles.row} ${showPrices ? '' : styles.noPrices} ${w.symbol === activeSymbol ? styles.current : ''}`}>
                  <span className={styles.left}>
                    <span className={styles.sym}>{w.symbol}</span>
                    {w.filing ? <span className={styles.filing}><span aria-hidden="true">● </span><span className="sr-only">Filing today: </span>{w.filing}</span>
                      : <span className={styles.exch}>{w.exchange}</span>}
                  </span>
                  {showPrices && (
                    <span className={styles.right}>
                      <LiveNumber className={styles.ltp} value={w.ltp} />
                      <ChangeText percent={w.changePercent} size="sm" />
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
